// =================================================================
// Engine: Weight module (PSD §4.2.1, §4.2.2 / TODO Task 1.1)
// Pure functions — no framework imports.
// =================================================================
import { Sex, WeightRule } from '@amino-pk/shared-types';
import { err, ok } from './types';
const CM_PER_IN = 2.54;
const BASELINE_MALE = 50.0;
const BASELINE_FEMALE = 45.5;
const KG_PER_IN_OVER_60 = 2.3;
const ADJBW_FACTOR = 0.4;
const OBESITY_THRESHOLD_RATIO = 1.2;
const CACHECTIC_RATIO = 0.8;
/**
 * Devine Ideal Body Weight (PSD §4.2.1).
 * Male: 50 + 2.3 × (inchesOver60). Female: 45.5 + 2.3 × (inchesOver60).
 * Patients ≤ 5 ft use the sex baseline.
 */
export function computeIBW(sex, heightCm) {
    if (!(heightCm > 0)) {
        return err('height-non-positive', 'Height must be positive.', 'heightCm');
    }
    const heightIn = heightCm / CM_PER_IN;
    const inchesOver60 = heightIn - 60;
    const baseline = sex === Sex.Male ? BASELINE_MALE : BASELINE_FEMALE;
    if (inchesOver60 <= 0) {
        // ≤ 5 ft — floored at baseline (handled by flag downstream)
        return ok(baseline);
    }
    return ok(baseline + KG_PER_IN_OVER_60 * inchesOver60);
}
/** Adjusted Body Weight = IBW + 0.4 × (ABW − IBW). */
export function computeAdjBW(ibwKg, abwKg) {
    return ibwKg + ADJBW_FACTOR * (abwKg - ibwKg);
}
/** Cachectic flag: ABW < 0.8 × IBW. */
export function isCachectic(ibwKg, abwKg) {
    return ibwKg > 0 && abwKg / ibwKg < CACHECTIC_RATIO;
}
/** Short stature flag: < 4 ft (121.9 cm). */
export function isVeryShortStature(heightCm) {
    return heightCm > 0 && heightCm < 121.9;
}
/**
 * Select dosing weight by the standard rule (PSD §4.2.2).
 * ABW < IBW → UseABW; ≤120% IBW → UseIBW; >120% IBW → UseAdjBW.
 */
export function selectDosingWeight(ibwKg, adjbwKg, abwKg) {
    if (!(ibwKg > 0)) {
        return err('ibw-zero', 'IBW must be greater than zero.', 'ibwKg');
    }
    if (abwKg < ibwKg) {
        return ok({ weight: abwKg, rule: WeightRule.UseABW });
    }
    const ratio = abwKg / ibwKg;
    if (ratio <= OBESITY_THRESHOLD_RATIO) {
        return ok({ weight: ibwKg, rule: WeightRule.UseIBW });
    }
    return ok({ weight: adjbwKg, rule: WeightRule.UseAdjBW });
}
/**
 * Orchestrator: compute the full DosingWeightResult from a patient input.
 * Applies an override if present.
 */
export function computeDosingWeight(patient, override) {
    const ibwRes = computeIBW(patient.sex, patient.heightCm);
    if (!ibwRes.ok)
        return ibwRes;
    const ibwKg = ibwRes.value;
    const abwKg = patient.actualWeightKg;
    const adjbwKg = computeAdjBW(ibwKg, abwKg);
    if (override) {
        return ok({
            ibwKg,
            adjbwKg,
            abwKg,
            selectedWeightKg: override.weightKg,
            selectionRule: WeightRule.ManualOverride,
            override: true,
            overrideWeightKg: override.weightKg,
            overrideReason: override.reason,
        });
    }
    const selRes = selectDosingWeight(ibwKg, adjbwKg, abwKg);
    if (!selRes.ok)
        return selRes;
    return ok({
        ibwKg,
        adjbwKg,
        abwKg,
        selectedWeightKg: selRes.value.weight,
        selectionRule: selRes.value.rule,
        override: false,
    });
}
//# sourceMappingURL=weight.js.map